import { useCallback, useSyncExternalStore } from "react";

type ToastVariant = "default" | "success" | "warning" | "error" | "info";

export interface ToastEntry {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
}

let toasts: ToastEntry[] = [];
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function addToast(variant: ToastVariant, title: string, description?: string) {
  const id = crypto.randomUUID();
  toasts = [...toasts, { id, title, description, variant }];
  emit();
}

function dismissToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

function getSnapshot() {
  return toasts;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const toast = {
  success: (title: string, description?: string) => addToast("success", title, description),
  error: (title: string, description?: string) => addToast("error", title, description),
  warning: (title: string, description?: string) => addToast("warning", title, description),
  info: (title: string, description?: string) => addToast("info", title, description),
  default: (title: string, description?: string) => addToast("default", title, description),
  dismiss: dismissToast,
};

export function useToasts() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useDismissToast() {
  return useCallback((id: string) => dismissToast(id), []);
}
